import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Button, Tab, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * Modal para configurar widgets personalizados
 * Permite al usuario seleccionar métricas, períodos, filtros, etc.
 */
const WidgetConfigModal = ({ show, onHide, widgetType, widgetDefinition, onSave, existingConfig = null }) => {
  const [config, setConfig] = useState({
    title: '',
    metric: '',
    period: 'month',
    comparison: true,
    client: '',
    campaign: '',
    chartType: 'line',
    color: '#2c7be5',
    showLegend: true,
    showLabels: true,
    limit: 10,
    sortBy: 'date',
    sortOrder: 'desc'
  });

  useEffect(() => {
    if (existingConfig) {
      setConfig(prev => ({ ...prev, ...existingConfig }));
    } else if (widgetDefinition) {
      setConfig(prev => ({
        ...prev,
        title: widgetDefinition.name
      }));
    }
  }, [existingConfig, widgetDefinition]);

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave({
      ...config,
      widgetType: widgetType
    });
  };

  if (!widgetDefinition) return null;

  const isKPI = widgetDefinition.category === 'kpi';
  const isChart = widgetDefinition.category === 'chart';
  const isTable = widgetDefinition.category === 'table';

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
          Configurar Widget: {widgetDefinition.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="general" className="mb-3">
          {/* Tab: General */}
          <Tab eventKey="general" title="General">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Título del Widget</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej: Leads de este mes"
                  value={config.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
                <Form.Text className="text-muted">
                  Nombre personalizado para tu widget
                </Form.Text>
              </Form.Group>

              {/* Métricas para KPI y Chart */}
              {(isKPI || isChart) && (
                <Form.Group className="mb-3">
                  <Form.Label>Métrica a Mostrar</Form.Label>
                  <Form.Select
                    value={config.metric}
                    onChange={(e) => handleChange('metric', e.target.value)}
                  >
                    <option value="">Selecciona una métrica...</option>
                    <optgroup label="Leads & Conversiones">
                      <option value="leads">Total de Leads</option>
                      <option value="conversions">Conversiones</option>
                      <option value="conversion_rate">Tasa de Conversión (%)</option>
                      <option value="qualified_leads">Leads Calificados</option>
                    </optgroup>
                    <optgroup label="Inversión & ROI">
                      <option value="investment">Inversión Total</option>
                      <option value="cost_per_lead">Costo por Lead (CPL)</option>
                      <option value="roi">Retorno de Inversión (ROI)</option>
                      <option value="roas">ROAS</option>
                    </optgroup>
                    <optgroup label="Campañas & Engagement">
                      <option value="impressions">Impresiones</option>
                      <option value="clicks">Clicks</option>
                      <option value="ctr">CTR (%)</option>
                      <option value="engagement_rate">Engagement Rate</option>
                    </optgroup>
                    <optgroup label="Ventas">
                      <option value="revenue">Ingresos</option>
                      <option value="orders">Órdenes</option>
                      <option value="avg_order_value">Valor Promedio de Orden</option>
                    </optgroup>
                  </Form.Select>
                </Form.Group>
              )}

              {/* Período */}
              <Form.Group className="mb-3">
                <Form.Label>Período de Tiempo</Form.Label>
                <Form.Select
                  value={config.period}
                  onChange={(e) => handleChange('period', e.target.value)}
                >
                  <option value="today">Hoy</option>
                  <option value="yesterday">Ayer</option>
                  <option value="week">Esta Semana</option>
                  <option value="month">Este Mes</option>
                  <option value="quarter">Este Trimestre</option>
                  <option value="year">Este Año</option>
                  <option value="last_7_days">Últimos 7 días</option>
                  <option value="last_30_days">Últimos 30 días</option>
                  <option value="last_90_days">Últimos 90 días</option>
                  <option value="custom">Personalizado</option>
                </Form.Select>
              </Form.Group>

              {/* Comparación para KPI */}
              {isKPI && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Mostrar comparación con período anterior"
                    checked={config.comparison}
                    onChange={(e) => handleChange('comparison', e.target.checked)}
                  />
                </Form.Group>
              )}
            </Form>
          </Tab>

          {/* Tab: Filtros */}
          <Tab eventKey="filters" title="Filtros">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Filtrar por Cliente</Form.Label>
                <Form.Select
                  value={config.client}
                  onChange={(e) => handleChange('client', e.target.value)}
                >
                  <option value="">Todos los clientes</option>
                  <option value="ford">Ford Ecuador</option>
                  <option value="utpl">UTPL</option>
                  <option value="trivo">Trivo</option>
                  <option value="danec">Danec</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Filtrar por Campaña</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nombre de campaña (opcional)"
                  value={config.campaign}
                  onChange={(e) => handleChange('campaign', e.target.value)}
                />
              </Form.Group>

              {isTable && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Ordenar por</Form.Label>
                    <Form.Select
                      value={config.sortBy}
                      onChange={(e) => handleChange('sortBy', e.target.value)}
                    >
                      <option value="date">Fecha</option>
                      <option value="investment">Inversión</option>
                      <option value="leads">Leads</option>
                      <option value="roi">ROI</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Orden</Form.Label>
                    <Form.Select
                      value={config.sortOrder}
                      onChange={(e) => handleChange('sortOrder', e.target.value)}
                    >
                      <option value="desc">Descendente</option>
                      <option value="asc">Ascendente</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Límite de Registros</Form.Label>
                    <Form.Control
                      type="number"
                      min="5"
                      max="100"
                      value={config.limit}
                      onChange={(e) => handleChange('limit', parseInt(e.target.value))}
                    />
                  </Form.Group>
                </>
              )}
            </Form>
          </Tab>

          {/* Tab: Apariencia (solo para Charts) */}
          {isChart && (
            <Tab eventKey="appearance" title="Apariencia">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Gráfico</Form.Label>
                  <Form.Select
                    value={config.chartType}
                    onChange={(e) => handleChange('chartType', e.target.value)}
                  >
                    <option value="line">Línea</option>
                    <option value="bar">Barras</option>
                    <option value="area">Área</option>
                    <option value="pie">Torta</option>
                    <option value="doughnut">Dona</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Color Principal</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      value={config.color}
                      onChange={(e) => handleChange('color', e.target.value)}
                      style={{ width: '60px', height: '38px' }}
                    />
                    <Form.Control
                      type="text"
                      value={config.color}
                      onChange={(e) => handleChange('color', e.target.value)}
                      placeholder="#2c7be5"
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Mostrar leyenda"
                    checked={config.showLegend}
                    onChange={(e) => handleChange('showLegend', e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Mostrar etiquetas"
                    checked={config.showLabels}
                    onChange={(e) => handleChange('showLabels', e.target.checked)}
                  />
                </Form.Group>
              </Form>
            </Tab>
          )}
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!config.title || !config.metric}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Guardar Configuración
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WidgetConfigModal;


